import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
export function ServerApiPage() {
  return <DocPageLayout title="Making a Server API Request" description="Learn how to authenticate and make API requests to the payment gateway from your server." data-id="element-1960">
      <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-4" data-id="element-1961">
        Authentication
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1962">
        All API requests must be authenticated using HTTP Basic Authentication.
        Your credentials consist of your Merchant ID and API Password, which are
        provided when you register for a merchant account.
      </p>
      <div className="overflow-x-auto my-6" data-id="element-1963">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1964">
          <thead data-id="element-1965">
            <tr className="bg-gray-50" data-id="element-1966">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1967">
                Credential
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1968">
                Format
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1969">
                Example
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1970">
            <tr data-id="element-1971">
              <td className="px-4 py-3 font-medium" data-id="element-1972">Username</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1973">
                merchant.{'{merchantId}'}
              </td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1974">
                merchant.TEST12345678
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1975">
              <td className="px-4 py-3 font-medium" data-id="element-1976">Password</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1977">Your API password</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1978">a1b2c3d4e5f6g7h8</td>
            </tr>
          </tbody>
        </table>
      </div>

      <CodeBlock language="cURL" title="Authentication Example" code={`curl https://test-gateway.mastercard.com/api/rest/version/72/merchant/TEST12345678/order/ORD-001/transaction/TXN-001 \\
  -u "merchant.TEST12345678:a1b2c3d4e5f6g7h8" \\
  -X PUT \\
  -H "Content-Type: application/json" \\
  -d '{
    "apiOperation": "PAY",
    "order": {
      "amount": "100.00",
      "currency": "USD"
    },
    "sourceOfFunds": {
      "type": "CARD",
      "provided": {
        "card": {
          "number": "5123450000000008",
          "securityCode": "100",
          "expiry": {
            "month": "05",
            "year": "25"
          }
        }
      }
    }
  }'`} data-id="element-1979" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1980">Base URL</h2>
      <p className="text-gray-700 mb-4" data-id="element-1981">
        Use the appropriate base URL for your environment:
      </p>
      <div className="overflow-x-auto my-6" data-id="element-1982">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1983">
          <thead data-id="element-1984">
            <tr className="bg-gray-50" data-id="element-1985">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1986">
                Environment
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1987">
                Base URL
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1988">
            <tr data-id="element-1989">
              <td className="px-4 py-3 font-medium" data-id="element-1990">Test</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600" data-id="element-1991">
                https://test-gateway.mastercard.com/api/rest/version/72
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1992">
              <td className="px-4 py-3 font-medium" data-id="element-1993">Production</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600" data-id="element-1994">
                https://gateway.mastercard.com/api/rest/version/72
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="warning" title="API Versioning" data-id="element-1995">
        Always specify the API version in your request URL. The current latest
        version is 72. Using an older version may result in missing features or
        deprecated behavior.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1996">
        Request Format
      </h2>
      <p className="text-gray-700 mb-4" data-id="element-1997">
        All requests and responses use JSON format. Set the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1998">
          Content-Type
        </code>{' '}
        header to{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1999">
          application/json
        </code>{' '}
        for all requests.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-2000">
        URL Structure
      </h2>
      <CodeBlock language="HTTP" title="URL Pattern" code={`{baseUrl}/merchant/{merchantId}/order/{orderId}/transaction/{transactionId}

# Examples:
# Create session:
POST /merchant/{merchantId}/session

# Pay:
PUT /merchant/{merchantId}/order/{orderId}/transaction/{transactionId}

# Retrieve order:
GET /merchant/{merchantId}/order/{orderId}

# Refund:
PUT /merchant/{merchantId}/order/{orderId}/transaction/{transactionId}`} data-id="element-2001" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-2002">
        Response Format
      </h2>
      <CodeBlock language="JSON" title="Successful Response" code={`{
  "merchant": "TEST12345678",
  "order": {
    "id": "ORD-001",
    "amount": 100.00,
    "currency": "USD",
    "status": "CAPTURED",
    "creationTime": "2025-01-15T10:30:00.000Z"
  },
  "response": {
    "acquirerCode": "00",
    "gatewayCode": "APPROVED"
  },
  "result": "SUCCESS",
  "transaction": {
    "amount": 100.00,
    "currency": "USD",
    "id": "TXN-001",
    "type": "PAYMENT"
  }
}`} data-id="element-2003" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-2004">
        Error Codes
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-2005">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-2006">
          <thead data-id="element-2007">
            <tr className="bg-gray-50" data-id="element-2008">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-2009">
                HTTP Code
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-2010">
                Gateway Code
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-2011">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-2012">
            <tr data-id="element-2013">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2014">200</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2015">APPROVED</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2016">
                Transaction approved successfully
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-2017">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2018">200</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2019">DECLINED</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2020">
                Transaction declined by issuer
              </td>
            </tr>
            <tr data-id="element-2021">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2022">400</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2023">INVALID_REQUEST</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2024">
                Request is malformed or missing required fields
              </td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-2025">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2026">401</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2027">
                AUTHENTICATION_FAILED
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2028">Invalid credentials</td>
            </tr>
            <tr data-id="element-2029">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2030">404</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2031">NOT_FOUND</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2032">Resource not found</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-2033">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2034">500</td>
              <td className="px-4 py-3 font-mono text-xs" data-id="element-2035">SYSTEM_ERROR</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-2036">
                Internal gateway error
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="tip" title="Idempotency" data-id="element-2037">
        Use unique order IDs and transaction IDs for each request. If you need
        to retry a request, use the same IDs to prevent duplicate transactions.
        The gateway treats requests with the same IDs as idempotent.
      </InfoBox>
    </DocPageLayout>;
}